/**
 * Workflow-type resolution for the "I want to set up a new project"
 * starter (sidebar, Projects dashboard, Ask Puntt home tile).
 *
 * For real customers, the starter routes to the `project_management`
 * skill — its overlay trains the model to ask for an asset upload, set
 * up stages, and orchestrate reviewer routing. That is the right UX
 * for a customer who is genuinely creating a project.
 *
 * Vitalora demo: the same starter is the entry point to the
 * baseline-capture conversation (Sarah Chen / Step 1 of Ronnie's
 * script). The `project_management` overlay fights the Vitalora
 * baseline-capture overlay layered on top by `chat.py`, and the
 * dominant "drop in your asset" directive distracts the model from
 * `record_demo_baseline`. The Haiku skill-detector subsequently
 * switches the chat to `puntt_value` (Workflow Delay Diagnosis) on
 * the first qualitative pain statement ("launch slips are what hurt
 * most"), which hallucinates the canned "Launch Slip Revenue
 * Analysis" canvas because `PUNTT_VALUE_PROMPT` has no awareness of
 * the demo skill.
 *
 * Routing the demo's New Project starter to `general` keeps the
 * conversation in the open-ended live-AI overlay defined in
 * `chat.py` so the buyer's facts get captured via
 * `record_demo_baseline` instead of routed to a workflow-delay canvas.
 *
 * See PR #3488 / the agent diagnosis in
 * `agent-transcripts/db0a935c-c7af-473e-a264-a706bb799808/...` for
 * the full evidence trail.
 */
import { isDemoEnterprise } from "../../utils/auth";

export type NewProjectWorkflowType = "project" | "general";

/**
 * Returns the workflowType that the "New Project" starter (in any of
 * its three render locations — Ask Puntt home tile, Projects
 * dashboard, AppSidebar) should send to christo when the user clicks
 * it.
 *
 * - Real tenants: "project" (project_management skill).
 * - Vitalora demo tenant (Enterprise.isDemo === true via the cached
 *   enterprises list): "general", so the live-AI baseline-capture
 *   overlay stays in charge.
 *
 * Pure function (reads from the react-query cache via
 * `isDemoEnterprise`). Safe to call outside React.
 */
export function resolveNewProjectWorkflowType(): NewProjectWorkflowType {
  return isDemoEnterprise() ? "general" : "project";
}
